import React, { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { evaluate, asDecimal, asFraction, numeric, SHOW } from "./calcEngine";
import { bigBtn, gcard } from "./gedKit";

const KEYS = [
  [["x²", "²"], ["^", "^"], ["√", "√("], ["n/d", "frac"], ["f◄►d", "toggle"]],
  [["(", "("], [")", ")"], ["(−)", "neg"], ["⌫", "back"], ["clear", "clear"]],
  [["7"], ["8"], ["9"], ["÷", "÷"]],
  [["4"], ["5"], ["6"], ["×", "×"]],
  [["1"], ["2"], ["3"], ["−", "−"]],
  [["0"], ["."], ["enter", "enter"], ["+", "+"]],
];

const TASKS = [
  { say: "Fractions: press 3, n/d, 4, then +, 1, n/d, 8, then enter.", tip: "The n/d key builds a stacked fraction.", ok: (v) => v.result === "7/8" },
  { say: "Now press f◄►d to switch 7/8 to a decimal.", tip: "f◄►d flips between fraction and decimal.", ok: (v) => v.mode === "dec" && v.value === 0.875 },
  { say: "Negative numbers: press clear, then (−), 6, +, 1, 0, enter.", tip: "(−) makes a number negative. The − key only subtracts.", ok: (v) => v.value === 4 && v.tokens[0] === "neg" },
  { say: "Try the wrong way once: press clear, then −, 6, +, 1, 0, enter.", tip: "See the error? Starting with the minus key doesn't work.", ok: (v) => v.error === "SYNTAX ERROR" },
  { say: "Square roots: press clear, √, 1, 4, 4, ), enter.", tip: "√ opens a bracket. Close it with ).", ok: (v) => v.value === 12 },
  { say: "Exponents: press clear, 2, ^, 5, enter.", tip: "^ raises to any power. x² is a shortcut for squared.", ok: (v) => v.value === 32 },
  { say: "Watch the brackets: press clear, (, (−), 3, ), x², enter.", tip: "(−3)² = 9, but (−)3² without brackets = −9.", ok: (v) => v.value === 9 && v.tokens.includes("(") },
];

const show = (t) => t.map((x) => SHOW[x] || x).join("");

export default function Calculator({ onDone }) {
  const [tokens, setTokens] = useState([]);
  const [out, setOut] = useState(null);
  const [task, setTask] = useState(0);
  const [passed, setPassed] = useState(false);
  const check = (st) => { if (!passed && TASKS[task]?.ok(st)) setPassed(true); };
  const press = (k) => {
    if (k === "clear") { setTokens([]); setOut(null); return; }
    if (k === "back") { setTokens(tokens.slice(0, -1)); return; }
    if (k === "toggle") {
      if (!out?.raw) return;
      const mode = out.mode === "dec" ? "frac" : "dec";
      const st = { ...out, mode, result: mode === "dec" ? asDecimal(out.raw) : asFraction(out.raw) };
      setOut(st); check(st); return;
    }
    if (k === "enter") {
      let st;
      try { const raw = evaluate(tokens); st = { raw, value: numeric(raw), result: asFraction(raw), mode: "frac", tokens, error: null }; }
      catch (e) { st = { error: e.message, result: e.message, tokens }; }
      setOut(st); check(st); return;
    }
    if (out) { setOut(null); if (!["+", "−", "×", "÷", "^", "²"].includes(k)) { setTokens([k]); return; } }
    setTokens([...tokens, k]);
  };
  const next = () => { setPassed(false); task + 1 < TASKS.length ? setTask(task + 1) : onDone(); };
  const t = TASKS[task];
  return (
    <div className="space-y-4" data-testid="lp-part-calculator">
      <div className={`${gcard} space-y-2`}>
        <div className="text-xs uppercase tracking-[0.25em] text-[#d4a017]">Practice {task + 1} of {TASKS.length}</div>
        <p className="text-lg" data-testid="calc-task">{t.say}</p>
        <p className="text-sm text-white/60">{t.tip}</p>
        {passed && <div data-testid="calc-pass" className="flex items-center gap-2 text-emerald-300 text-base"><CheckCircle2 className="h-5 w-5" /> You got it.</div>}
        {passed && <button type="button" onClick={next} className={bigBtn} data-testid="calc-next">{task + 1 < TASKS.length ? "Next key" : "Finish Part 2"}</button>}
      </div>
      <div className="rounded-3xl bg-[#1d2330] border border-white/15 p-3 space-y-3 max-w-sm mx-auto" data-testid="calc">
        <div className="rounded-xl bg-[#c9d4c1] text-[#14201a] px-3 py-2 min-h-[84px] font-mono">
          <div data-testid="calc-expr" className="text-lg break-all min-h-[28px]">{show(out?.tokens || tokens) || " "}</div>
          <div data-testid="calc-result" className={`text-right text-2xl font-bold ${out?.error ? "text-red-700 text-base" : ""}`}>{out ? out.result : ""}</div>
        </div>
        <div className="space-y-2">
          {KEYS.map((row, ri) => (
            <div key={ri} className={`grid gap-2 ${row.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}>
              {row.map(([label, k = label]) => (
                <button key={label} type="button" onClick={() => press(k)} data-testid={`calc-key-${k === "√(" ? "sqrt" : k === "²" ? "sq" : k === "÷" ? "div" : k === "×" ? "mul" : k === "−" ? "minus" : k === "+" ? "plus" : k === "^" ? "pow" : k === "." ? "dot" : k === "(" ? "lp" : k === ")" ? "rp" : k}`}
                  className={`min-h-[48px] rounded-lg font-semibold active:scale-95 transition-transform ${k === "enter" ? "bg-[#d4a017] text-black text-sm" : /^[0-9.]$/.test(k) ? "bg-white/90 text-black text-lg" : k === "neg" || k === "toggle" || k === "frac" ? "bg-sky-700 text-white text-sm" : "bg-[#3a4357] text-white text-sm"}`}>{label}</button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
